import { useEffect, useState } from "react";

const POLL_INTERVAL_MS = 60_000;

function isPreviewOrDevHost(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === "undefined") return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com")) return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  return false;
}

export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (isPreviewOrDevHost()) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const currentId =
      typeof __BUILD_ID__ !== "undefined" ? String(__BUILD_ID__) : "";

    const check = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (!data?.buildId || !currentId) return;
        if (String(data.buildId) !== currentId) {
          if (!cancelled) {
            setUpdateAvailable(true);
            if (timer) clearInterval(timer);
          }
        }
      } catch {
        // ignore transient network errors
      }
    };

    // initial delay so we don't fetch during first paint
    const initial = setTimeout(check, 5_000);
    timer = setInterval(check, POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return updateAvailable;
}
